import React from 'react';
import 'banner-pattern/dist/main.css';

interface ErrorBannerProps {
    currentStep: number;
    errors: Record<string, string>;
    fieldLabels?: Record<string, string>;
    onFieldClick?: (fieldName: string) => void;
    atLeastOneError?: string;
    requiredIfErrors?: Record<string, string>;
    dateRangeError?: { field: string; message: string } | null;
}

/**
 * Maps technical error messages to user-friendly display messages
 */
const getDisplayErrorMessage = (fieldLabel: string, errorMessage: string): string => {
    if (errorMessage.includes('10-digit phone number') || errorMessage.includes('Invalid phone number')) {
        return `${fieldLabel} number is not valid. Enter a 10-digit number without spaces or special characters.`;
    }
    if (errorMessage.includes('Invalid email')) {
        return `${fieldLabel} is not valid. Enter a valid email address.`;
    }
    if (errorMessage.includes('required') || errorMessage.includes('is required')) {
        return `${fieldLabel} is required.`;
    }
    if (errorMessage.includes('MM/DD/YYYY') || errorMessage.includes('date format')) {
        return `${fieldLabel}: must be in a valid date format. Use MM/DD/YYYY.`;
    }
    if (errorMessage.includes('2023') || errorMessage.includes('on or after')) {
        return `${fieldLabel} should not be prior to 2023.`;
    }
    if (errorMessage.includes('out of range')) {
        return `${fieldLabel} cannot be earlier than today.`;
    }
    if (errorMessage.includes('Class Start Date should be before Class End Date.')) {
        return `${fieldLabel} should be before Class end Date..`;
    }
    if (errorMessage.includes('Class Fee is required when')) {
        return 'Class Fee is required when Class Start Date or Class End Date is entered.';
    }
    if (errorMessage.includes('must not exceed') && errorMessage.includes('characters')) {
        return `${fieldLabel} exceed the allowed character limit. Reduce the text to 500 characters or fewer.`;
    }
    if (errorMessage.includes('Enter a valid numeric amount.')) {
        return `${fieldLabel} must contain numbers only.`;
    }
    return `${fieldLabel}: ${errorMessage}`;
};

const scrollToField = (fieldName: string) => {
    const fieldElement = document.getElementById(fieldName) ||
        document.getElementById(`date-input-${fieldName}`) ||
        document.getElementById(`textarea-${fieldName}`);
    
    if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldElement.focus();
    }
};

const ViewFieldButton: React.FC<{ fieldName: string; onFieldClick?: (fieldName: string) => void }> = ({ fieldName, onFieldClick }) => (
    <button
        type="button"
        onClick={() => {
            scrollToField(fieldName);
            onFieldClick?.(fieldName);
        }}
        className='!text-[#0074AD] underline'
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
    >
        View field.
    </button>
);

const N400ApplicationDateError: React.FC<{ onFieldClick?: (fieldName: string) => void }> = ({ onFieldClick }) => (
    <>
        <p className="!m-0 !text-[20px]">
            <strong>N400 Application date is required</strong>
        </p>
        <p className="!mb-0 !mt-[6px]">
            An N-400 application date is required when a filing fee is entered. Please use the format MM/DD/YYYY.{' '}
            <ViewFieldButton fieldName="n400ApplicationDate" onFieldClick={onFieldClick} />
        </p>
    </>
);

const TotalClassFeesError: React.FC<{ onFieldClick?: (fieldName: string) => void }> = ({ onFieldClick }) => (
    <>
        <p className="!m-0 text-[20px]">
            <strong>Total class fees is required</strong>
        </p>
        <p className="!mb-0 !mt-[6px]">
            Class dates have been entered, but the Total class fees is missing. Please add a fee to ensure complete and accurate data.{' '}
            <ViewFieldButton fieldName="totalClassFees" onFieldClick={onFieldClick} />
        </p>
    </>
);

const ClassDetailsError: React.FC<{
    requiredIfErrors: Record<string, string>;
    fieldLabels: Record<string, string>;
    onFieldClick?: (fieldName: string) => void;
}> = ({ requiredIfErrors, fieldLabels, onFieldClick }) => (
    <>
        <p className="!m-0 text-[20px]">
            <strong>Please complete all required class details before continuing:</strong>
        </p>
        <p className="!mb-0 !mt-[6px]">Update the following fields</p>
        <ul className='!my-0 !ml-[16px]'>
            {Object.entries(requiredIfErrors).map(([fieldName]) => {
                const fieldLabel = fieldLabels[fieldName] || fieldName;
                return (
                    <li key={fieldName} className="text-[#0074AD] list-disc">
                        <button
                            type="button"
                            onClick={() => {
                                scrollToField(fieldName);
                                onFieldClick?.(fieldName);
                            }}
                            className='text-[#0074AD] underline'
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                            {fieldLabel}{fieldLabel.endsWith('date') ? ' (MM/DD/YYYY)' : ''}
                        </button>
                    </li>
                );
            })}
        </ul>
    </>
);

const DateRangeErrorContent: React.FC<{ 
    dateRangeError: { field: string; message: string }; 
    onFieldClick?: (fieldName: string) => void 
}> = ({ dateRangeError, onFieldClick }) => (
    <div>
        <p className="!m-0 !text-[20px]">
            <strong>Invalid date range</strong>
        </p>
        <p className="!mb-0 !mt-[6px]">
            The Class Start Date must be earlier than the Class End Date. Please adjust the end date to continue.{' '}
            <ViewFieldButton fieldName={dateRangeError.field} onFieldClick={onFieldClick} />
        </p>
    </div>
);

const RequiredIfErrorContent: React.FC<{
    requiredIfErrors: Record<string, string>;
    fieldLabels: Record<string, string>;
    onFieldClick?: (fieldName: string) => void;
}> = ({ requiredIfErrors, fieldLabels, onFieldClick }) => {
    if (requiredIfErrors.n400ApplicationDate) {
        return <N400ApplicationDateError onFieldClick={onFieldClick} />;
    }
    if (requiredIfErrors.totalClassFees) {
        return <TotalClassFeesError onFieldClick={onFieldClick} />;
    }
    return <ClassDetailsError requiredIfErrors={requiredIfErrors} fieldLabels={fieldLabels} onFieldClick={onFieldClick} />;
};

const StandardErrorList: React.FC<{
    errors: Record<string, string>;
    currentStep: number;
    fieldLabels: Record<string, string>;
    onFieldClick?: (fieldName: string) => void;
}> = ({ errors, currentStep, fieldLabels, onFieldClick }) => (
    <div className="mt-2">
        {Object.entries(errors).map(([fieldName, errorMessage]) => {
            const fieldLabel = fieldLabels[fieldName] || fieldName;
            const displayMessage = getDisplayErrorMessage(fieldLabel, errorMessage);

            return (
                <div key={fieldName} className="!mb-0">
                    {currentStep === 1 && (
                        <>
                            {displayMessage}
                            {onFieldClick && (
                                <>
                                    {' '}
                                    <ViewFieldButton fieldName={fieldName} onFieldClick={onFieldClick} />
                                </>
                            )}
                        </>
                    )}

                    {currentStep === 3 && (
                        <ul className='!my-0 !ml-[16px]'>
                            <li className="text-[#0074AD] list-disc">
                                <button
                                    type="button"
                                    onClick={() => {
                                        scrollToField(fieldName);
                                        onFieldClick?.(fieldName);
                                    }}
                                    className='text-[#0074AD] underline'
                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                >
                                    {displayMessage}
                                </button>
                            </li>
                        </ul>
                    )}
                </div>
            );
        })}
    </div>
);

const getBannerTitle = (
    currentStep: number,
    atLeastOneError?: string,
    requiredIfErrorCount?: number,
    dateRangeError?: { field: string; message: string } | null
): string => {
    if (atLeastOneError) return "Please enter at least one fee to continue.";
    if (requiredIfErrorCount && requiredIfErrorCount > 0) return "";
    if (dateRangeError) return "";
    if (currentStep === 1) return "Please fix the following issue before submitting:";
    if (currentStep === 3) return "There are errors that need your attention before submission:";
    return "";
};

const getBannerId = (
    atLeastOneError?: string,
    requiredIfErrorCount?: number,
    dateRangeError?: { field: string; message: string } | null
): string => {
    if (atLeastOneError) return "atleastone-error-banner-title";
    if (requiredIfErrorCount && requiredIfErrorCount > 0) return "requiredif-error-banner-title";
    if (dateRangeError) return "daterange-error-banner-title";
    return "error-banner-title";
};

/**
 * Renders the appropriate error content based on error type
 */
interface RenderErrorContentOptions {
    atLeastOneError?: string;
    requiredIfErrorCount: number;
    requiredIfErrors?: Record<string, string>;
    dateRangeError?: { field: string; message: string } | null;
    errors: Record<string, string>;
    currentStep: number;
    fieldLabels: Record<string, string>;
    onFieldClick?: (fieldName: string) => void;
}

const renderErrorContent = (options: RenderErrorContentOptions): React.ReactNode => {
    const {
        atLeastOneError,
        requiredIfErrorCount,
        requiredIfErrors,
        dateRangeError,
        errors,
        currentStep,
        fieldLabels,
        onFieldClick
    } = options;

    if (atLeastOneError) {
        return (
            <p className="!m-0">
                You only need to fill in one of the fee fields below to proceed.
            </p>
        );
    }
    
    if (requiredIfErrorCount > 0 && requiredIfErrors) {
        return (
            <RequiredIfErrorContent 
                requiredIfErrors={requiredIfErrors} 
                fieldLabels={fieldLabels} 
                onFieldClick={onFieldClick} 
            />
        );
    }
    
    if (dateRangeError) {
        return (
            <DateRangeErrorContent 
                dateRangeError={dateRangeError} 
                onFieldClick={onFieldClick} 
            />
        );
    }
    
    return (
        <StandardErrorList 
            errors={errors} 
            currentStep={currentStep} 
            fieldLabels={fieldLabels} 
            onFieldClick={onFieldClick} 
        />
    );
};

const ErrorBanner: React.FC<ErrorBannerProps> = ({ 
    currentStep, 
    errors, 
    fieldLabels = {}, 
    onFieldClick, 
    atLeastOneError, 
    requiredIfErrors, 
    dateRangeError 
}) => {
    const errorCount = Object.keys(errors).length;
    const requiredIfErrorCount = Object.keys(requiredIfErrors || {}).length;

    if (errorCount === 0 && !atLeastOneError && requiredIfErrorCount === 0 && !dateRangeError) {
        return null;
    }

    const bannerId = getBannerId(atLeastOneError, requiredIfErrorCount, dateRangeError);
    const bannerTitle = getBannerTitle(currentStep, atLeastOneError, requiredIfErrorCount, dateRangeError);

    return (
        <div
            className="ds-banner ds-banner__bg--white ds-banner--icon !p-[24px] !mb-[24px] !border-red-600"
            data-ds-variant="basic"
            data-ds-version="1"
            data-ds-theme="vessel"
            aria-labelledby={bannerId}
            role="alert"
            aria-live="assertive"
        >
            <div className="ds-banner__icon-img">
                <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt=""
                    style={{ width: '42px', height: '42px' }}
                />
            </div>
            <div className="ds-banner__container">
                <div className="ds-banner__main flex flex-col">
                    <div
                        className="ds-banner__main-header--awareness"
                        id={bannerId}
                        style={{ fontWeight: 350, fontSize: '20px', marginBottom: '6px' }}
                    >
                        {bannerTitle}
                    </div>

                    {currentStep === 3 && !atLeastOneError && requiredIfErrorCount === 0 && !dateRangeError && (
                        <p className='p3 !my-0'>
                            Some fields contain invalid or missing information. Review the details below and update the highlighted fields to proceed.
                        </p>
                    )}

                    <div className="ds-banner__main-body" style={{ fontSize: '16px' }}>
                        {renderErrorContent({
                            atLeastOneError,
                            requiredIfErrorCount,
                            requiredIfErrors,
                            dateRangeError,
                            errors,
                            currentStep,
                            fieldLabels,
                            onFieldClick
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ErrorBanner;
